import { Component, Input, OnInit } from '@angular/core';
import { SegurosApiService } from '../../services/seguros-api.service';
import {
  TudocFunnelKPIs,
  TudocFunnelStepCount,
  TudocAbandonedClient,
  TudocPagination
} from '../../models/seguros.interface';

/**
 * Tracking TuDoc — vista de recuperacion de clientes que abandonaron el flujo.
 *
 * Alimentada por 3 endpoints (plan 21):
 *   GET /api/insurance/tudoc/funnel-summary       KPIs + drop-off por step
 *   GET /api/insurance/tudoc/abandoned-clients    1 row por persona + contact
 *   GET /api/insurance/tudoc/traces               (no usado aca; para deep-dive)
 *
 * La agrupacion es por `person_code`: si un cliente intento varias veces,
 * aparece 1 row con `attempts` = N (badge en la tabla).
 */

const STEP_LABELS: Record<string, string> = {
  FLOW_STARTED: 'Abrio TuDoc',
  PRICES_QUERIED: 'Vio precio',
  BENEFICIARY_QUERIED: 'Consulto CI',
  RATES_MODAL_OPENED: 'Abrio modal duracion',
  MONTHS_SELECTED: 'Selecciono meses',
  ABANDONED_AT_MODAL: 'Abandono en duracion',
  FORM_STARTED: 'Inicio formulario',
  COMMITMENT_MODAL_OPENED: 'Abrio compromiso',
  COMMITMENT_ACCEPTED: 'Acepto compromiso',
  ABANDONED_AT_COMMITMENT_MODAL: 'Abandono en compromiso',
  FORM_SUBMITTED: 'Envio formulario',
  ABANDONED_AT_FORM: 'Abandono en formulario',
  RESUMED_PENDING_PAYMENT: 'Retomo pago',
  PAYMENT_METHOD_SELECTED: 'Selecciono metodo pago',
  INVOICE_PREPARED: 'Factura preparada',
  PAYMENT_INITIATED: 'Inicio pago',
  ABANDONED_AT_PAYMENT: 'Abandono en pago',
  PAYMENT_SUMMARY_VIEWED: 'Vio resumen pago',
  PAYMENT_SUMMARY_CONTINUE: 'Continuo desde resumen',
  ACTIVATE_POST_PAYMENT: 'Activando post-pago',
  RESULT_VIEWED: 'Vio resultado',
  RESULT_RESOLVED: 'Servicio activo',
  RESULT_RETRY_STARTED: 'Reintento activacion',
  RESULT_RETRY_RESULT: 'Reintento OK',
  RESULT_RETRY_FAILED: 'Reintento fallo',
  POLICY_CREATED: 'Poliza creada (backend)',
  RENEWAL_QUOTE_PREPARED: 'Renovacion preparada',
  PAYMENT_CONFIRMED: 'Pago confirmado (backend)',
  TUDOC_CALL_STARTED: 'Llamando gateway',
  TUDOC_NOTIFIED: 'Gateway notificado',
  RENEWED: 'Renovado',
  NOTIFICATION_FAILED: 'Fallo notificacion aliado'
};

const STEP_SEVERITY: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
  RESULT_RESOLVED: 'success',
  TUDOC_NOTIFIED: 'success',
  RENEWED: 'success',
  NOTIFICATION_FAILED: 'danger',
  RESULT_RETRY_FAILED: 'danger',
  ABANDONED_AT_MODAL: 'warning',
  ABANDONED_AT_COMMITMENT_MODAL: 'warning',
  ABANDONED_AT_FORM: 'warning',
  ABANDONED_AT_PAYMENT: 'warning'
};

@Component({
  selector: 'app-tudoc-tracking',
  templateUrl: './tudoc-tracking.component.html',
  styleUrls: ['./tudoc-tracking.component.css']
})
export class TudocTrackingComponent implements OnInit {
  @Input() insurerCode: string = 'VE';

  // Filters (bound to inputs).
  startDate: string = '';
  endDate: string = '';
  stepFilter: string = '';
  searchQuery: string = '';   // filtro client-side por cedula/nombre

  // KPIs + funnel.
  kpis: TudocFunnelKPIs | null = null;
  funnel: TudocFunnelStepCount[] = [];
  funnelMaxCount = 0;

  // Abandoned clients table.
  clients: TudocAbandonedClient[] = [];
  pagination: TudocPagination = { currentPage: 1, totalPages: 0, totalItems: 0, limit: 20 };

  // UI state.
  loadingSummary = false;
  loadingClients = false;
  loadError: string | null = null;

  // Steps sugeridos para el dropdown de filtro (subset de los mas comunes).
  availableSteps = [
    'ABANDONED_AT_MODAL',
    'ABANDONED_AT_COMMITMENT_MODAL',
    'ABANDONED_AT_FORM',
    'ABANDONED_AT_PAYMENT',
    'NOTIFICATION_FAILED',
    'FLOW_STARTED',
    'PRICES_QUERIED',
    'MONTHS_SELECTED',
    'FORM_STARTED',
    'FORM_SUBMITTED',
    'PAYMENT_METHOD_SELECTED',
    'PAYMENT_INITIATED'
  ];

  constructor(private api: SegurosApiService) {}

  ngOnInit(): void {
    this.loadAll();
  }

  loadAll(): void {
    this.loadSummary();
    this.loadClients(1);
  }

  loadSummary(): void {
    this.loadingSummary = true;
    this.loadError = null;
    this.api.getTudocFunnelSummary({
      startDate: this.startDate || undefined,
      endDate: this.endDate || undefined
    }).subscribe({
      next: (r) => {
        this.kpis = r.kpis;
        this.funnel = r.funnel || [];
        this.funnelMaxCount = this.funnel.reduce((max, s) => Math.max(max, s.count), 0);
        this.loadingSummary = false;
      },
      error: (err) => {
        this.loadingSummary = false;
        this.loadError = err?.error?.message || 'Error cargando KPIs';
      }
    });
  }

  loadClients(page: number): void {
    this.loadingClients = true;
    this.api.getTudocAbandonedClients({
      startDate: this.startDate || undefined,
      endDate: this.endDate || undefined,
      step: this.stepFilter || undefined,
      page,
      limit: this.pagination.limit
    }).subscribe({
      next: (r) => {
        this.clients = r.clients || [];
        this.pagination = r.pagination;
        this.loadingClients = false;
      },
      error: (err) => {
        this.loadingClients = false;
        this.loadError = err?.error?.message || 'Error cargando clientes';
      }
    });
  }

  applyFilters(): void {
    this.loadAll();
  }

  clearFilters(): void {
    this.startDate = '';
    this.endDate = '';
    this.stepFilter = '';
    this.searchQuery = '';
    this.loadAll();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.pagination.totalPages) return;
    this.loadClients(page);
  }

  // Filtro client-side por cedula/nombre.
  get filteredClients(): TudocAbandonedClient[] {
    const q = (this.searchQuery || '').trim().toLowerCase();
    if (!q) return this.clients;
    return this.clients.filter(c =>
      (c.cedula || '').toLowerCase().includes(q) ||
      (c.nombre || '').toLowerCase().includes(q) ||
      (c.apellidos || '').toLowerCase().includes(q)
    );
  }

  // Labels y colores por step.
  stepLabel(step: string): string {
    return STEP_LABELS[step] || step;
  }

  stepBadgeClass(step: string): string {
    const sev = STEP_SEVERITY[step] || 'info';
    return `badge-${sev}`;
  }

  // Formato de tiempo relativo simple.
  timeAgo(iso: string | Date | undefined | null): string {
    if (!iso) return '-';
    const then = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.floor((now - then) / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} h`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d} d`;
    const mo = Math.floor(d / 30);
    return `${mo} mes${mo > 1 ? 'es' : ''}`;
  }

  formatDateShort(iso: string | Date | undefined | null): string {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString('es-VE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  // Ancho de la barra del funnel (%).
  funnelBarWidth(count: number): number {
    if (!this.funnelMaxCount) return 0;
    return Math.round((count / this.funnelMaxCount) * 100);
  }

  // CTAs de contacto.
  ctaCall(client: TudocAbandonedClient): void {
    if (!client.phone) return;
    window.location.href = `tel:${client.phone}`;
  }

  ctaWhatsApp(client: TudocAbandonedClient): void {
    if (!client.phone) return;
    const cleaned = String(client.phone).replace(/[^\d]/g, '');
    const msg = encodeURIComponent(
      `Hola ${client.nombre || ''}, te contactamos de CrediTotal. Vimos que iniciaste la afiliacion a TuDoc y no completaste el proceso. ¿Podemos ayudarte a terminarlo?`
    );
    window.open(`https://wa.me/${cleaned}?text=${msg}`, '_blank');
  }

  ctaEmail(client: TudocAbandonedClient): void {
    if (!client.email) return;
    const subject = encodeURIComponent('Completa tu afiliacion TuDoc');
    const body = encodeURIComponent(
      `Hola ${client.nombre || ''},\n\nNotamos que iniciaste la afiliacion a TuDoc pero no completaste el proceso. Podemos ayudarte a terminarlo — responde este correo o llamanos.\n\nEquipo CrediTotal`
    );
    window.location.href = `mailto:${client.email}?subject=${subject}&body=${body}`;
  }

  // Monto ultimo intento (si aplica).
  clientAmount(client: TudocAbandonedClient): string {
    const md = client.last_metadata || {};
    const usd = md['amount_usd'] || md['price_usd_final'];
    if (usd == null) return '-';
    return `$${Number(usd).toFixed(2)}`;
  }

  clientMonths(client: TudocAbandonedClient): string {
    const md = client.last_metadata || {};
    const months = md['months'];
    if (months == null) return '-';
    return `${months} mes${months > 1 ? 'es' : ''}`;
  }
}
